import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import html2pdf from 'html2pdf.js';
import '../../../../component-styles/Proposal-UI.css';
import notify from '../../../../utils/notify';

import HeaderSection from './HeaderSection';
import { DOCUMENT_TITLE_MAX_LENGTH } from '../../../../utils/documentTitle.js';
import InfoSection from './InfoSection';
import CatalogSection from './CatalogSection';
import MemoTagsSection from './MemoTagsSection';
import AttachmentsSection from './AttachmentsSection';
import TotalsSection from './TotalsSection';
import ProposalPayments from '../ProposalPayments';
import ProposalActions from './ProposalsActions';
import ProposalPdfDocument from '../ProposalPdfDocument';
import SendEmailModal from '../../../../components/common/SendEmailModal';
import TearSheetModal from '../TearSheetModal';
import TearSheetPdfDocument from '../TearSheetPdfDocument';
import ConvertToInvoiceModal from '../ConvertToInvoiceModal';
import ConvertToPoBlockersModal from '../ConvertToPoBlockersModal';


import LineItemCard from './LineItems/LineItemCard';
import AddItemForm from '../../../../components/ProductsServices/AddItemForm';
import API_BASE from '../../../../utils/api.js';
import {
  getConvertToPoBlockers,
  unassignedItemsNotice,
} from '../../../../utils/convertToPoMessage.js';
import usePermissions from '../../../../auth/usePermissions';
import { buildPublicProposalUrl } from '../../../../utils/publicLinks.js';
import { buildTearSheetPdf } from '../../../../utils/tearSheetPdf.js';
import { toDateInputValue } from '../../../../utils/invoiceAdjustments.js';
import {
  getProposalInvoicedAmount,
  getProposalInvoicingStatus,
  isInvoicedLineItem,
} from '../../../../utils/proposalInvoicing.js';
import ConfirmModal from '../../../../components/utils/ConfirmModal';
import ActivityFeed from '../../../../components/shared/ActivityFeed';
import {
  getCatalogMarkup,
  getDefaultMarkupByItemType,
  sortRoomsForDesignFlow,
} from '../../../../utils/designWorkflow.js';
import {
  autoCreateQuickBooksProposal,
  autoUpdateQuickBooksProposal,
  shouldAutoCreateInQuickBooks,
} from '../../../../utils/quickbooksAutoSync.js';

/* ----------------------------------------------------
   UTILITIES
----------------------------------------------------- */

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v) => Number((toNum(v)).toFixed(2));

const fmt2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const normalizeMarkupValue = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(String(value).replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

/* ----------------------------------------------------
   MAIN COMPONENT
----------------------------------------------------- */

export default function ProposalForm({ onClose, onSaved, project, initial, depositToRecord }) {
  const { permissions } = usePermissions();
  const canEdit = permissions.createEditInvoicesProposals !== false;
  const canApplyPayments = permissions.applyInvoiceProposalPayments !== false;

  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();
  const pdfRef = useRef(null);
  const proposalPdfRef = useRef(null);
  const tearSheetPdfRef = useRef(null);
  const [showTearSheetModal, setShowTearSheetModal] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState({ open: false, email: '', publicUrl: '', projectId: '', proposalId: '' });

  const [editHeader, setEditHeader] = useState(false);
  const initializedRef = useRef(false);
  const [proposalStatus, setProposalStatus] = useState(initial?.status || 'draft');
  const projectDefaultMarkup = useMemo(
    () => normalizeMarkupValue(project?.defaultMarkup, 25),
    [project?.defaultMarkup]
  );
  const projectDefaultShippingMarkup = useMemo(
    () => normalizeMarkupValue(project?.defaultShippingMarkup, 0),
    [project?.defaultShippingMarkup]
  );
  const defaultMarkupForItem = (item) => ({
    type: '%',
    value: getDefaultMarkupByItemType(item, {
      ...project,
      defaultFfeMarkup: project?.defaultFfeMarkup ?? projectDefaultMarkup,
    }),
  });

  /* ----------------------------------------------------
     TAX SETTINGS (LOAD ONCE)
  ----------------------------------------------------- */

  const [taxLocations, setTaxLocations] = useState([]);
  const [quickbooksSettings, setQuickbooksSettings] = useState(null);
  const [qbConnected, setQbConnected] = useState(false);
  const [company, setCompany] = useState(null);
  const [proposalTemplate, setProposalTemplate] = useState(null);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [defaultShippingDestination, setDefaultShippingDestination] = useState('designer_warehouse');
  const [freightDestinations, setFreightDestinations] = useState([]);

  useEffect(() => {
    if (!userId) return;

    (async () => {
      try {
        const [settingsRes, qbStatusRes] = await Promise.all([
          fetch(`${API_BASE}/api/settings/${userId}`),
          fetch(`${API_BASE}/api/quickbooks/status`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('idToken')}` },
          }),
        ]);
        if (!settingsRes.ok) return;

        const settings = await settingsRes.json();
        setQuickbooksSettings(settings?.quickbooks || null);
        setCompany(settings?.company || null);
        setProposalTemplate(settings?.proposalTemplate || null);
        setMessageTemplates(Array.isArray(settings?.messageTemplates) ? settings.messageTemplates : []);
        setDefaultShippingDestination(settings?.defaultShippingDestination || 'designer_warehouse');
        setFreightDestinations(Array.isArray(settings?.freightDestinations) ? settings.freightDestinations : []);

        if (qbStatusRes.ok) {
          const qbStatus = await qbStatusRes.json().catch(() => ({}));
          setQbConnected(Boolean(qbStatus?.connected));
        }

        const normalized = (settings.taxLocations || []).map((t, idx) => {
          const parsedRate =
            typeof t.tax === 'string'
              ? Number(t.tax.replace('%', '')) / 100
              : Number(t.rate ?? t.tax);
          const rate = Number.isFinite(parsedRate) ? parsedRate : 0;

          return {
            ...t,
            id: t.id || `tax-${idx}`,
            rate,
            tax: rate,
          };
        });

        setTaxLocations(normalized);
      } catch (err) {
        console.error('Failed to load tax settings', err);
      }
    })();
  }, [API_BASE, userId]);

  /* ----------------------------------------------------
     RESOLVE PROJECT TAX RATE (ID → DECIMAL)
  ----------------------------------------------------- */

  const resolvedSalesTaxRate = useMemo(() => {
    if (!project?.salesTaxRate) return 0;

    const match = taxLocations.find((t) => t.id === project.salesTaxRate);

    return match?.rate ?? 0;
  }, [project?.salesTaxRate, taxLocations]);

  /* ----------------------------------------------------
     FORM STATE
  ----------------------------------------------------- */

  const initialForm = {
    client: {
      name:
        initial?.client?.name ||
        project?.customerName ||
        `${project?.customerFirst || ''} ${project?.customerLast || ''}`.trim(),
      email: initial?.client?.email || project?.email || '',
    },
    projectId: initial?.projectId ?? project?.id ?? project?.projectId ?? '',
    projectSite:
      initial?.projectSite ||
      (project?.projectStreet
        ? `${project.projectStreet}, ${project.projectCity} ${project.projectState} ${project.projectZip}`
        : ''),
    billingAddress:
      initial?.billingAddress ||
      (project?.billingStreet
        ? `${project.billingStreet}, ${project.billingCity} ${project.billingState} ${project.billingZip}`
        : ''),
    salesTaxRate:
      initial?.salesTaxRate != null ? toNum(initial.salesTaxRate) : resolvedSalesTaxRate,
    procurementFeePct:
      initial?.procurementFeePct != null
        ? toNum(initial.procurementFeePct)
        : normalizeMarkupValue(project?.procurementFeePct, 0),
    notes: initial?.notes || project?.notes || '',
    attachments: initial?.attachments || [],
    // Local today, not UTC: after ~8pm Eastern the UTC date is already tomorrow.
    date: initial?.date || toDateInputValue(new Date()),
    dueDate: initial?.dueDate || '',
    title: initial?.title || '',
  };

  const [form, setForm] = useState(initialForm);
  const [savedProposal, setSavedProposal] = useState(initial || null);
  const [vendorModal, setVendorModal] = useState({ open: false, vendors: [] });
  // What's stopping Convert to PO, while the designer sorts it out
  const [poBlockers, setPoBlockers] = useState(null);
  // { amount } — the deposit to take a payment for before converting again
  const [depositPaymentRequest, setDepositPaymentRequest] = useState(null);
  const depositToRecordHandledRef = useRef(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  const resolvedTaxLocation = useMemo(() => {
    const snapshotLocationId =
      savedProposal?.tax?.locationId ??
      initial?.tax?.locationId ??
      savedProposal?.tax?.location?.id ??
      initial?.tax?.location?.id ??
      project?.salesTaxRate ??
      null;

    if (snapshotLocationId) {
      const matchedById = taxLocations.find((t) => t.id === snapshotLocationId);
      if (matchedById) return matchedById;
    }

    const snapshotRate = savedProposal?.tax?.rate ?? initial?.tax?.rate ?? form.salesTaxRate;

    if (Number.isFinite(Number(snapshotRate))) {
      return (
        taxLocations.find((t) => Math.abs(Number(t.rate || 0) - Number(snapshotRate)) < 0.0001) ||
        null
      );
    }

    return null;
  }, [
    form.salesTaxRate,
    initial?.tax?.location?.id,
    initial?.tax?.locationId,
    initial?.tax?.rate,
    project?.salesTaxRate,
    savedProposal?.tax?.location?.id,
    savedProposal?.tax?.locationId,
    savedProposal?.tax?.rate,
    taxLocations,
  ]);

  useEffect(() => {
    if (!project) return;

    setForm((prev) => ({
      ...prev,
      client: {
        name:
          project.customerName ||
          `${project.customerFirst || ''} ${project.customerLast || ''}`.trim(),
        email: project.email || prev.client.email,
      },
      projectId: project.id ?? project.projectId ?? prev.projectId,
      projectSite: project.projectStreet
        ? `${project.projectStreet}, ${project.projectCity} ${project.projectState} ${project.projectZip}`
        : prev.projectSite,
      billingAddress: project.billingStreet
        ? `${project.billingStreet}, ${project.billingCity} ${project.billingState} ${project.billingZip}`
        : prev.billingAddress,
      salesTaxRate: resolvedSalesTaxRate,
      procurementFeePct:
        prev.procurementFeePct != null
          ? prev.procurementFeePct
          : normalizeMarkupValue(project?.procurementFeePct, 0),
    }));
  }, [project, resolvedSalesTaxRate]);

  // Seed Notes / Payment Terms from the project's general notes, but only
  // once for a brand-new proposal — never re-sync on later project refetches,
  // which would otherwise clobber whatever the user has typed into this
  // proposal's own Notes field.
  const projectNotesSeededRef = useRef(false);
  useEffect(() => {
    if (initial || projectNotesSeededRef.current) return;
    if (!project?.notes) return;
    setForm((prev) => (prev.notes ? prev : { ...prev, notes: project.notes }));
    projectNotesSeededRef.current = true;
  }, [project, initial]);

  // Default the Notes / Payment Terms field from the studio's Proposal
  // Template settings, but only for a brand-new proposal (never overwrite
  // notes carried over from an existing proposal or the project record).
  useEffect(() => {
    if (initial) return;
    if (!proposalTemplate?.defaultNotes) return;
    setForm((prev) => (prev.notes ? prev : { ...prev, notes: proposalTemplate.defaultNotes }));
  }, [proposalTemplate, initial]);

  /* ----------------------------------------------------
     PROPOSAL NUMBER
  ----------------------------------------------------- */

  const [proposalNumber, setProposalNumber] = useState(initial?.proposalNumber || '');

  useEffect(() => {
    if (initial?.proposalNumber) return;
    if (!form.projectId) return;

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/proposals/latest?userId=${encodeURIComponent(
            userId
          )}&projectId=${encodeURIComponent(form.projectId)}`
        );
        if (!res.ok) throw new Error('Failed to load proposal number');
        const data = await res.json();
        if (data?.proposalNumber) {
          setProposalNumber(data.proposalNumber);
        }
      } catch {
        setProposalNumber('P-00001');
      }
    })();
  }, [API_BASE, userId, initial, form.projectId]);

  /* ----------------------------------------------------
     LINE ITEMS
  ----------------------------------------------------- */

  const [items, setItems] = useState(() =>
    (initial?.lineItems || []).map((it) => ({
      ...it,
      productItemId: it.productItemId ?? null,
      libraryItemId: it.libraryItemId ?? null,
      source: it.source || 'proposal',
      tradePrice: toNum(it.tradePrice),
      shipping: toNum(it.shipping),
      tariff: toNum(it.tariff),
      prepaidTax: toNum(it.prepaidTax),
      clientUnit: toNum(it.clientUnit),
      deposit: toNum(it.deposit),
      qty: toNum(it.qty),
      markup: it.markup || defaultMarkupForItem(it),
      shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
      tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
      room: it.room || '',
      shippingDestination: it.shippingDestination || 'designer_warehouse',
      clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
      updatedAt: it.updatedAt || null,
    }))
  );

  const removedIds = useRef([]);

  const moveItem = (id, direction) => {
    setItems((prev) => {
      const index = prev.findIndex((it) => it.itemId === id);
      const targetIndex = index + direction;
      if (index === -1 || targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.itemId === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => {
    removedIds.current.push(id);
    setItems((prev) => prev.filter((it) => it.itemId !== id));
  };

  const genId = () => `itm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const addItemFromCatalog = (it) => {
    setItems((prev) => [
      ...prev,
      {
        ...it,
        itemId: genId(),
        productItemId: it.itemId || it.productItemId || null,
        // Catalog items are not library items; let backend sync create/link.
        libraryItemId: null,
        source: 'library',
        tradePrice: toNum(it.tradePrice),
        unitShipping: toNum(it.shipping),
        unitTariff: toNum(it.tariff),
        shipping: toNum(it.shipping),
        tariff: toNum(it.tariff),
        prepaidTax: 0,
        deposit: 0,
        qty: 1,
        clientUnit: toNum(it.clientUnit),
        markup: it.markup || defaultMarkupForItem(it),
        shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
        tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
        room: '',
        shippingDestination: defaultShippingDestination,
        clientApproved: null,
      },
    ]);
  };

  /* ----------------------------------------------------
     ROOMS
  ----------------------------------------------------- */

  const [rooms, setRooms] = useState([]);

  const handleRoomCreated = (newRoom) => {
    setRooms((prev) => {
      const exists = prev.some((r) => r.sectionId === newRoom.sk);
      if (exists) return prev;
      return [...prev, { ...newRoom, sectionId: newRoom.sectionId || newRoom.sk }];
    });
  };

  async function refreshRooms() {
    if (!userId) return;

    // ?sectionsOnly=true skips the per-item usage hydration (N+1 queries) since we only need sections
    const url = form.projectId
      ? `${API_BASE}/api/library/${userId}/${form.projectId}?sectionsOnly=true`
      : `${API_BASE}/api/library/${userId}`;

    try {
      const res = await fetch(url, { cache: 'no-store' });
      const data = await res.json();

      setRooms(
        sortRoomsForDesignFlow(
          (data.sections || [])
            .filter((s) => s.type === 'section')
            .map((s) => ({ ...s, sectionId: s.sectionId || s.sk }))
        )
      );
    } catch {}
  }

  useEffect(() => {
    refreshRooms();
  }, [userId, form.projectId]);

  useEffect(() => {
    if (!userId || !form.projectId) return undefined;

    const intervalId = window.setInterval(() => {
      refreshRooms();
    }, 30000);

    const handleFocus = () => {
      refreshRooms();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId, form.projectId]);

  /* ----------------------------------------------------
     SCHEDULES
  ----------------------------------------------------- */

  const [schedules, setSchedules] = useState([]);

  const handleScheduleCreated = (newSchedule) => {
    setSchedules((prev) => {
      const exists = prev.some((s) => s.sk === newSchedule.sk);
      if (exists) return prev;
      return [...prev, newSchedule].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  useEffect(() => {
    if (!userId || !form.projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(form.projectId)}`,
          { cache: 'no-store' }
        );
        const data = await res.json();
        setSchedules(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch schedules:', err);
      }
    })();
  }, [userId, form.projectId]);

  /* ----------------------------------------------------
     CATALOG
  ----------------------------------------------------- */

  const [catalog, setCatalog] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [q, setQ] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  const refreshCatalog = async () => {
    try {
      setCatalogLoading(true);
      const res = await fetch(`${API_BASE}/api/products/${userId}`, { cache: 'no-store' });

      const data = await res.json();

      const normalized = (Array.isArray(data) ? data : data.Items || data.items || []).map(
        (it) => ({
          itemId: it.itemId || it.id || genId(),
          name: it.name || '',
          sku: it.sku || '',
          description: it.description || '',
          imageUrl: it.imageUrl || '',
          vendorName: it.vendorName || '',
          salesCode: it.salesCode || '',
          type: it.type || (it.category === 'Service' ? 'service' : 'product'),
          tradePrice: toNum(it.tradePrice ?? it.price ?? 0),
          msrp: toNum(it.msrp ?? 0),
          link: it.link || '',
          color: it.color || '',
          finishes: it.finishes || '',
          materials: it.materials || '',
          customSpecs: Array.isArray(it.customSpecs) ? it.customSpecs : [],
          dimensions: {
            length: it.length ?? it.dimensions?.length ?? '',
            width: it.width ?? it.dimensions?.width ?? '',
            height: it.height ?? it.dimensions?.height ?? '',
            depth: it.depth ?? it.dimensions?.depth ?? '',
          },
          qty: 1,
          unitType: 'Each',
          shipping: toNum(it.shipping ?? 0),
          tariff: toNum(it.tariff ?? 0),
          prepaidTax: 0,
          markup: getCatalogMarkup(it, defaultMarkupForItem(it)),
          shippingMarkup: { type: '%', value: projectDefaultShippingMarkup },
          tariffMarkup: { type: '%', value: 0 },
          deposit: 0,
          taxable: true,
          shippingTaxable: false,
          tariffTaxable: false,
          shippingDestination: defaultShippingDestination,
          clientApproved: null,
        })
      );

      setCatalog(normalized);
    } catch (e) {
      setCatalogError(e.message);
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    refreshCatalog();
  }, []);

  /* ----------------------------------------------------
     COMPUTED ITEMS (FINAL PRICE LOGIC)
  ----------------------------------------------------- */

  const computedItems = useMemo(() => {
    return items.map((it) => {
      const trade = toNum(it.tradePrice);
      const qty = toNum(it.qty);

      const computedSell =
        it.markup?.type === '%'
          ? trade + trade * (Number(it.markup.value) / 100)
          : trade + Number(it.markup.value);

      const clientUnitRaw =
        it.lastEdited === 'sell' && Number.isFinite(Number(it.clientUnit))
          ? Number(it.clientUnit)
          : computedSell;
      const clientUnit = round2(Math.max(clientUnitRaw, 0));

      const ship = toNum(it.shipping);
      const shipMarkup =
        it.shippingMarkup?.type === '%'
          ? ship * (Number(it.shippingMarkup.value) / 100)
          : Number(it.shippingMarkup.value);

      const clientShip = round2(ship + shipMarkup);

      const clientTariff = round2(toNum(it.tariff));
      const lineMerch = round2(clientUnit * qty);
      const lineTotal = round2(lineMerch + clientShip + clientTariff);

      return {
        ...it,
        clientUnit,
        clientShipping: clientShip,
        clientTariff,
        lineMerch,
        lineTotal,
      };
    });
  }, [items]);

  /* ----------------------------------------------------
     TOTALS
  ----------------------------------------------------- */

  const totals = useMemo(() => {
    const subtotal = computedItems.reduce((s, it) => s + toNum(it.lineTotal), 0);
    const procurementFeePct = normalizeMarkupValue(form.procurementFeePct, 0);

    const taxableMerch = round2(
      computedItems
        .filter((it) => it.taxable)
        .reduce((s, it) => s + it.lineMerch, 0)
    );

    const taxableShipping = round2(
      computedItems
        .filter((it) => it.shippingTaxable && toNum(it.qty) > 0)
        .reduce((s, it) => s + it.clientShipping, 0)
    );

    const taxableTariff = round2(
      computedItems
        .filter((it) => it.tariffTaxable && toNum(it.qty) > 0)
        .reduce((s, it) => s + it.clientTariff, 0)
    );

    const tax = round2((taxableMerch + taxableShipping + taxableTariff) * toNum(form.salesTaxRate));
    const procurementFee = round2(subtotal * (procurementFeePct / 100));
    const grandTotal = round2(subtotal + tax + procurementFee);

    return {
      subtotal: round2(subtotal),
      tax,
      procurementFee,
      grandTotal,
    };
  }, [computedItems, form.salesTaxRate, form.procurementFeePct]);

  /* ----------------------------------------------------
     TAGS
  ----------------------------------------------------- */

  const [tags, setTags] = useState(initial?.tags || []);

  useEffect(() => {
    if (!initial || initializedRef.current) return;
    initializedRef.current = true;

    setSavedProposal(initial);
    setProposalStatus(initial.status || 'draft');
    setProposalNumber(initial.proposalNumber || proposalNumber);
    setTags(initial.tags || []);
    setForm((prev) => ({
      ...prev,
      client: initial.client || prev.client,
      projectId: initial.projectId || prev.projectId,
      projectSite: initial.projectSite || prev.projectSite,
      billingAddress: initial.billingAddress || prev.billingAddress,
      salesTaxRate: initial.salesTaxRate != null ? toNum(initial.salesTaxRate) : prev.salesTaxRate,
      procurementFeePct:
        initial.procurementFeePct != null ? toNum(initial.procurementFeePct) : prev.procurementFeePct,
      notes: initial.notes || prev.notes,
      attachments: initial.attachments || prev.attachments,
      date: initial.date || prev.date,
      dueDate: initial.dueDate || prev.dueDate,
      title: initial.title ?? prev.title,
    }));
    setItems(
      (initial.lineItems || []).map((it) => ({
        ...it,
        productItemId: it.productItemId ?? null,
        libraryItemId: it.libraryItemId ?? null,
        source: it.source || 'proposal',
        tradePrice: toNum(it.tradePrice),
        shipping: toNum(it.shipping),
        tariff: toNum(it.tariff),
        prepaidTax: toNum(it.prepaidTax),
        clientUnit: toNum(it.clientUnit),
        deposit: toNum(it.deposit),
        qty: toNum(it.qty),
        markup: it.markup || defaultMarkupForItem(it),
        shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
        tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
        room: it.room || '',
        shippingDestination: it.shippingDestination || 'designer_warehouse',
        clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
        updatedAt: it.updatedAt || null,
      }))
    );
  }, [initial, proposalNumber]);


  /* ----------------------------------------------------
     SAVE PROPOSAL
  ----------------------------------------------------- */

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const saveInFlightRef = useRef(false);

  const effectiveStatus = proposalStatus || savedProposal?.status || initial?.status || 'draft';
  const normalizedStatus = String(effectiveStatus || 'draft').toLowerCase();
  const hasPaid = Number(savedProposal?.totals?.paid || initial?.totals?.paid || 0) > 0;
  const isLocked = normalizedStatus === 'approved' && hasPaid;
  // Once items are invoiced the totals also show what's left to bill here.
  const invoicedAmount = getProposalInvoicedAmount(savedProposal);
  const proposalPaid = Number(savedProposal?.totals?.paid || 0);

  useEffect(() => {
    if (isLocked && editHeader) {
      setEditHeader(false);
    }
  }, [isLocked, editHeader]);

  const buildPayload = () => ({
    userId,
    proposalNumber,
    title: form.title,
    companyName: project?.companyName,
    projectName: project?.projectNickname || project?.name,
    projectSite: form.projectSite,
    billingAddress: form.billingAddress,
    shippingAddress: form.projectSite,
    client: form.client,
    projectId: form.projectId,
    salesTaxRate: toNum(form.salesTaxRate), // snapshot decimal
    procurementFeePct: normalizeMarkupValue(form.procurementFeePct, 0),
    notes: form.notes,
    attachments: form.attachments,
    date: form.date,
    dueDate: form.dueDate || null,
    tags,
    lineItems: computedItems,
    tax: {
      rate: toNum(form.salesTaxRate),
      locationId:
        savedProposal?.tax?.locationId ??
        initial?.tax?.locationId ??
        resolvedTaxLocation?.id ??
        project?.salesTaxRate ??
        null,
      location:
        resolvedTaxLocation ?? savedProposal?.tax?.location ?? initial?.tax?.location ?? null,
    },
    totals,
    status: effectiveStatus,
    shareToken: savedProposal?.shareToken ?? initial?.shareToken,
  });

  async function saveDraft(stayOpen = false, options = {}) {
    if (saveInFlightRef.current) return null;
    if (!form.client.email) return setError('Client email required.');
    if (items.length === 0) return setError('Add at least one item.');

    saveInFlightRef.current = true;
    setSaving(true);
    setError(null);

    const nextStatus = options?.status || proposalStatus || effectiveStatus;

    try {
      const existingProposalId = savedProposal?.proposalId || initial?.proposalId;
      const url = existingProposalId
        ? `${API_BASE}/api/proposals/${userId}/${form.projectId}/${existingProposalId}`
        : `${API_BASE}/api/proposals`;
      const method = existingProposalId ? 'PUT' : 'POST';

      const basePayload = buildPayload();
      const payload = {
        ...basePayload,
        status: nextStatus,
      };
      if (nextStatus === 'sent') {
        payload.sentAt = new Date().toISOString();
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save proposal.');

      const saved = await res.json();
      setSavedProposal(saved);
      setProposalStatus(saved.status || nextStatus || 'draft');
      if (Array.isArray(saved?.lineItems)) {
        setItems(
          saved.lineItems.map((it) => ({
            ...it,
            productItemId: it.productItemId ?? null,
            libraryItemId: it.libraryItemId ?? null,
            source: it.source || 'proposal',
            tradePrice: toNum(it.tradePrice),
            shipping: toNum(it.shipping),
            tariff: toNum(it.tariff),
            prepaidTax: toNum(it.prepaidTax),
            clientUnit: toNum(it.clientUnit),
            deposit: toNum(it.deposit),
            qty: toNum(it.qty),
            markup: it.markup || defaultMarkupForItem(it),
            shippingMarkup: it.shippingMarkup || { type: '%', value: projectDefaultShippingMarkup },
            tariffMarkup: it.tariffMarkup || { type: '%', value: 0 },
            room: it.room || '',
            shippingDestination: it.shippingDestination || 'designer_warehouse',
            clientApproved: it.clientApproved === true ? true : it.clientApproved === false ? false : null,
            updatedAt: it.updatedAt || null,
          }))
        );
      }

      // QB sync and room refresh run in the background — don't block the save response
      if (
        qbConnected &&
        shouldAutoCreateInQuickBooks({ quickbooks: quickbooksSettings }) &&
        saved?.proposalId &&
        form.projectId
      ) {
        const qbSync = saved?.quickbooks?.estimateId
          ? autoUpdateQuickBooksProposal({ proposalId: saved.proposalId, projectId: form.projectId })
          : autoCreateQuickBooksProposal({ proposalId: saved.proposalId, projectId: form.projectId });

        qbSync
          .then((qbResult) => {
            if (qbResult?.skipped) notify.warning(qbResult.warning, { duration: 12000 });
            return fetch(
              `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
                form.projectId
              )}/${encodeURIComponent(saved.proposalId)}`,
              { cache: 'no-store' }
            );
          })
          .then((r) => (r.ok ? r.json() : null))
          .then((refreshed) => { if (refreshed) setSavedProposal(refreshed); })
          .catch((qbErr) => {
            setError(
              `Proposal saved, but QuickBooks auto-sync failed: ${
                qbErr.message || 'Unable to sync proposal.'
              }`
            );
          });
      }

      // Only refresh rooms when staying open (rooms panel is visible); skip on close
      if (stayOpen) refreshRooms();

      if (!stayOpen) onSaved?.(saved);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
      saveInFlightRef.current = false;
    }
  }

  const emailClient = async () => {
    const saved = await saveDraft(true, { status: 'sent' });
    if (!saved?.proposalId) return setError("We couldn't save this document. Please try again.");

    const proposalUrl = buildPublicProposalUrl(window.location.origin, saved.shareToken);
    const primaryEmail = saved?.client?.email || form.client?.email || project?.email || '';
    const clientEmail = [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', ');
    setSendEmailModal({
      open: true,
      email: clientEmail,
      publicUrl: proposalUrl,
      projectId: saved.projectId || form.projectId || '',
      proposalId: saved.proposalId,
    });
  };

  const requestEdit = () => {
    if (!isLocked) {
      setEditHeader(true);
      return;
    }
    setShowEditWarning(true);
  };

  const revokeApproval = async () => {
    if (!savedProposal?.proposalId || !form.projectId) return;
    setRevoking(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(form.projectId)}/${encodeURIComponent(
          savedProposal.proposalId
        )}/revoke-approval`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', userid: userId },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to revoke approval.');
        return;
      }
      if (data?.proposal) {
        setSavedProposal(data.proposal);
      }
      setShowEditWarning(false);
      setEditHeader(true);
    } catch (err) {
      console.error(err);
      notify('Failed to revoke approval.');
    } finally {
      setRevoking(false);
    }
  };

  const refreshProposal = async () => {
    if (!savedProposal?.proposalId || !form.projectId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(savedProposal.proposalId)}`,
        { cache: 'no-store' }
      );
      if (!res.ok) return;
      const data = await res.json();
      setSavedProposal(data);
      setProposalStatus(data?.status || proposalStatus);
    } catch {}
  };

  const [convertSource, setConvertSource] = useState(null);

  const convertToInvoice = async () => {
    if (!savedProposal?.proposalId || !form.projectId) return;

    // The invoice is built from the proposal as saved, so pick from that.
    let latest = savedProposal;
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(savedProposal.proposalId)}`,
        { cache: 'no-store' }
      );
      if (res.ok) {
        latest = await res.json();
        setSavedProposal(latest);
      }
    } catch {}

    if (getProposalInvoicingStatus(latest) === 'invoiced') {
      notify('Every item on this proposal has already been invoiced.');
      return;
    }
    setConvertSource(latest);
  };

  const submitConversion = async (itemIds) => {
    const res = await fetch(`${API_BASE}/api/proposals/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        projectId: form.projectId,
        proposalId: savedProposal.proposalId,
        itemIds,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      notify(data.error || 'Failed to convert proposal.');
      setConvertSource(null);
      refreshProposal();
      return;
    }

    const invoice = await res.json().catch(() => ({}));
    setConvertSource(null);
    if (!invoice?.invoiceId) return;

    navigate(`/projects/${form.projectId}/invoices/${invoice.invoiceId}`, {
      state: { project, invoice },
    });
  };

  const createPurchaseOrdersFromProposal = async (splitByVendor) => {
    const res = await fetch(
      `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
        form.projectId
      )}/${encodeURIComponent(savedProposal.proposalId)}/convert-to-po`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ splitByVendor }),
      }
    );

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      notify(data.error || 'Failed to convert to purchase order.');
      return false;
    }

    const poIds = Array.isArray(data.poIds) && data.poIds.length ? data.poIds : [data.openedPoId || data.poId].filter(Boolean);
    const poId = data.openedPoId || poIds[0];
    if (poId) {
      const state = { project };
      if (poIds.length > 1) {
        // Multiple draft POs were created (split by vendor) — queue the rest so
        // "Save & Close" on this one advances straight into the next instead of
        // dropping the user back on the proposal after only the first PO.
        state.poQueue = poIds.filter((id) => id !== poId);
        state.poQueueTotal = poIds.length;
        state.poQueuePosition = 1;
      }
      navigate(`/projects/${form.projectId}/purchase-orders/${poId}`, { state });
    }
    return true;
  };

  // The server decides whether the saved proposal can be converted and, if not,
  // says why and what fixes it.
  const convertToPurchaseOrder = async () => {
    if (!savedProposal?.proposalId || !form.projectId) return;

    try {
      const preflight = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(savedProposal.proposalId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dryRun: true }),
        }
      );
      const preflightData = await preflight.json().catch(() => ({}));

      if (!preflight.ok) {
        const blockers = getConvertToPoBlockers(preflightData);
        if (blockers.length > 0) {
          setPoBlockers(blockers);
          return;
        }
        notify(preflightData.error || 'Failed to check vendors.');
        return;
      }
      setPoBlockers(null);

      if (Array.isArray(preflightData.vendors) && preflightData.vendors.length > 1) {
        setVendorModal({
          open: true,
          vendors: preflightData.vendors,
          unassignedCount: preflightData.unassignedCount || 0,
        });
        return;
      }

      askConfirm({
        title: 'Create Purchase Order',
        message:
          'Create a purchase order from this proposal? A new draft PO will be generated from the proposal line items.' +
          unassignedItemsNotice(preflightData.unassignedCount),
        confirmLabel: 'Create PO',
        action: () => createPurchaseOrdersFromProposal(false),
      });
    } catch (err) {
      console.error('Convert proposal to PO error', err);
      notify('Unable to convert to purchase order.');
    }
  };

  // For a client who approved some other way (email, a signed copy, paying in
  // QuickBooks). Only the status changes — unsaved edits on the form stay as
  // they are — then the conversion picks back up.
  const markApprovedAndConvertToPO = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
          form.projectId
        )}/${encodeURIComponent(savedProposal.proposalId)}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'approved' }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        notify(data.error || 'Failed to mark the proposal approved.');
        return;
      }
    } catch (err) {
      console.error('Mark proposal approved error', err);
      notify('Failed to mark the proposal approved.');
      return;
    }

    setSavedProposal((prev) => (prev ? { ...prev, status: 'approved' } : prev));
    setProposalStatus('approved');
    notify(`${proposalNumber || 'Proposal'} marked as approved.`);
    await convertToPurchaseOrder();
  };

  // Record Payment: open the payment form for what's still owed on the deposit,
  // then pick Convert to PO back up once the payment is saved.
  const recordDepositThenConvert = (amount) => {
    setPoBlockers(null);
    setDepositPaymentRequest({ amount });
  };

  const handleManualPaymentClosed = (recorded) => {
    if (!depositPaymentRequest) return;
    setDepositPaymentRequest(null);
    if (recorded) convertToPurchaseOrder();
  };

  // Arriving from the proposals list's Record Payment fix.
  useEffect(() => {
    if (!depositToRecord || depositToRecordHandledRef.current || !savedProposal?.proposalId) return;
    depositToRecordHandledRef.current = true;
    setDepositPaymentRequest({ amount: depositToRecord.amount });
  }, [depositToRecord, savedProposal?.proposalId]);

  const poBlockerActions = {
    mark_approved: markApprovedAndConvertToPO,
    send_to_client: () => {
      setPoBlockers(null);
      emailClient();
    },
    ...(canApplyPayments && {
      record_payment: (blocker) => recordDepositThenConvert(blocker?.depositOutstanding),
    }),
  };

  const confirmSplitProposalVendors = async () => {
    try {
      await createPurchaseOrdersFromProposal(true);
    } catch (err) {
      console.error('Convert proposal to PO error', err);
      notify('Unable to convert to purchase order.');
    } finally {
      setVendorModal({ open: false, vendors: [] });
    }
  };

  const downloadPDF = async () => {
    if (!proposalPdfRef.current) return;
    const el = proposalPdfRef.current;
    const imgs = Array.from(el.querySelectorAll('img'));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    );
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Proposal-${proposalNumber || savedProposal?.proposalId || 'proposal'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(el)
      .save();
  };

  const [tearSheetConfig, setTearSheetConfig] = useState(null);
  const [tearSheetItems, setTearSheetItems] = useState(null);

  async function openTearSheetModal() {
    const proposalId = savedProposal?.proposalId;
    const pId = form.projectId || project?.id || project?.projectId;
    if (proposalId && pId && userId) {
      try {
        const token = localStorage.getItem('idToken');
        const res = await fetch(
          `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(pId)}/${encodeURIComponent(proposalId)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' }
        );
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.lineItems)) setTearSheetItems(data.lineItems);
        }
      } catch {}
    }
    setShowTearSheetModal(true);
  }

  // TearSheetPdfDocument routes item images through the image proxy itself, so
  // the items are handed over untouched.
  const handleTearSheetDownload = ({ title, options }) => {
    setShowTearSheetModal(false);
    setTearSheetConfig({ title, options, mode: 'download' });
  };

  // Renders the tear sheet PDF and uploads it (rather than downloading it
  // locally) so it can be linked from the "Send Proposal" email. Returns a
  // Promise that resolves with the uploaded file's URL.
  function generateTearSheetForEmail() {
    return new Promise((resolve, reject) => {
      setTearSheetConfig({
        title: project?.customerName || project?.name || form.projectSite || 'Tear Sheet',
        options: {
          showImage: true,
          showRoom: true,
          showDescription: true,
          showVendor: true,
          showMaterials: true,
          showColorFinish: true,
          showDimensions: true,
          showCustomSpecs: true,
        },
        mode: 'upload',
        resolve,
        reject,
      });
    });
  }

  async function uploadTearSheetBlob(blob, filename) {
    const token = localStorage.getItem('idToken');
    const presignUrl = `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(filename)}&type=${encodeURIComponent('application/pdf')}&size=${encodeURIComponent(blob.size)}`;
    const presignRes = await fetch(presignUrl, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
    if (!presignRes.ok) throw new Error('Failed to get upload URL');
    const { uploadUrl, fileUrl } = await presignRes.json();
    const uploadRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: blob });
    if (!uploadRes.ok) throw new Error('Failed to upload tear sheet');
    return fileUrl;
  }

  useEffect(() => {
    if (!tearSheetConfig || !tearSheetPdfRef.current) return;
    const el = tearSheetPdfRef.current;
    const safeTitle = tearSheetConfig.title.replace(/[/\\?%*:|"<>]/g, '-');
    let cancelled = false;
    const imgs = Array.from(el.querySelectorAll('img'));
    Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
      )
    ).then(async () => {
      if (cancelled) return;
      const filename = `${safeTitle}-TearSheets.pdf`;
      try {
        const pdf = await buildTearSheetPdf(el);
        if (tearSheetConfig.mode === 'upload') {
          const fileUrl = await uploadTearSheetBlob(pdf.output('blob'), filename);
          tearSheetConfig.resolve?.(fileUrl);
        } else {
          pdf.save(filename);
        }
      } catch (err) {
        if (tearSheetConfig.mode === 'upload') {
          tearSheetConfig.reject?.(err);
        } else {
          console.error('Tear sheet PDF error', err);
          notify('Failed to generate tear sheets.');
        }
      } finally {
        setTearSheetConfig(null);
        setTearSheetItems(null);
      }
    });
    return () => { cancelled = true; };
  }, [tearSheetConfig]);

  /* ----------------------------------------------------
     RENDER
  ----------------------------------------------------- */
  const proposalLabel = proposalNumber || '';

  return (
    <div className={`doc-page ${isLocked ? 'proposal-locked' : ''}`}>
      {/* Hidden clean PDF template */}
      <div className="prop-pdf-render-root" aria-hidden="true">
        <div ref={proposalPdfRef}>
          <ProposalPdfDocument
            proposalNumber={proposalLabel}
            computedItems={computedItems}
            totals={totals}
            form={form}
            project={project}
            company={company}
            apiBase={API_BASE}
            template={proposalTemplate}
            notes={form.notes}
          />
        </div>
      </div>

      <div ref={pdfRef}>
        <HeaderSection proposalNumber={proposalLabel} title={form.title} onClose={onClose} />

        <div className="if-card" style={{ marginTop: 12 }}>
          <div className="po-grid four">
            <div className="po-field">
              <label>Name (optional)</label>
              <input
                className="if-input"
                type="text"
                value={form.title}
                maxLength={DOCUMENT_TITLE_MAX_LENGTH}
                placeholder="e.g. Living Room"
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="po-field">
              <label>Status</label>
              <select
                className="if-select"
                value={proposalStatus}
                disabled={!canEdit}
                onChange={(e) => setProposalStatus(e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="changes_requested">Changes Requested</option>
                {/* Only imported history is archived; it isn't offered for other proposals. */}
                {proposalStatus === 'archived' && <option value="archived">Archived</option>}
              </select>
            </div>

            <div className="po-field">
              <label>Date</label>
              <input
                className="if-input"
                type="date"
                value={form.date ? String(form.date).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </div>

            <div className="po-field">
              <label>Due Date</label>
              <input
                className="if-input"
                type="date"
                value={form.dueDate ? String(form.dueDate).slice(0, 10) : ''}
                disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <InfoSection
          form={form}
          setForm={setForm}
          editHeader={editHeader}
          setEditHeader={setEditHeader}
          project={project}
          isLocked={isLocked}
          onRequestEdit={requestEdit}
          readOnly={!canEdit}
        />

        <div
          className={`proposal-editable ${isLocked ? 'locked' : ''}`}
          onClickCapture={(e) => {
            if (!isLocked) return;
            e.preventDefault();
            e.stopPropagation();
            requestEdit();
          }}
        >
          {isLocked && !showEditWarning && (
            <div className="proposal-lock-cover" onClick={requestEdit}>
              <div className="proposal-lock-cover-msg">
                Paid proposals are locked. Click to edit.
              </div>
            </div>
          )}
          <div className="proposal-editable-content">
            <div className="line-items-section">
              {computedItems.map((it, idx) => (
                <LineItemCard
                  key={it.itemId}
                  it={it}
                  updateItem={updateItem}
                  removeItem={removeItem}
                  rooms={rooms}
                  schedules={schedules}
                  onScheduleCreated={handleScheduleCreated}
                  userId={userId}
                  projectId={form.projectId}
                  onRoomCreated={handleRoomCreated}
                  catalog={catalog}
                  hideDeposit
                  freightDestinations={freightDestinations}
                  // Invoiced items are billed on their invoice; the proposal
                  // keeps them for reference only.
                  readOnly={!canEdit || isInvoicedLineItem(it)}
                  invoicedAs={isInvoicedLineItem(it) ? it.convertedInvoiceNumber || 'invoice' : ''}
                  onMoveUp={() => moveItem(it.itemId, -1)}
                  onMoveDown={() => moveItem(it.itemId, 1)}
                  isFirst={idx === 0}
                  isLast={idx === computedItems.length - 1}
                />
              ))}
            </div>

            {canEdit && (
              <CatalogSection
                catalog={catalog}
                q={q}
                setQ={setQ}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                catalogLoading={catalogLoading}
                catalogError={catalogError}
                addItemFromCatalog={addItemFromCatalog}
                setShowAddItemModal={setShowAddItemModal}
                fmtUSD={fmt2}
              />
            )}

            <MemoTagsSection
              form={form}
              setForm={setForm}
              tags={tags}
              setTags={setTags}
              readOnly={!canEdit}
              messageTemplates={messageTemplates}
            />

            <AttachmentsSection form={form} setForm={setForm} readOnly={!canEdit} />

            <TotalsSection
              totals={totals}
              fmtUSD={fmt2}
              {...(invoicedAmount > 0 && {
                invoiced: invoicedAmount,
                paid: proposalPaid,
                amountDue: Math.max(totals.grandTotal - invoicedAmount - proposalPaid, 0),
                dueLabel: 'Balance Due',
              })}
            />
          </div>
        </div>

      </div>

      {savedProposal?.proposalId && (
        <ProposalPayments
          proposal={savedProposal}
          projectId={form.projectId}
          onUpdated={refreshProposal}
          quickbooksConnected={qbConnected}
          prefillManualPayment={depositPaymentRequest}
          onManualPaymentClosed={handleManualPaymentClosed}
        />
      )}

      <ProposalActions
        onClose={onClose}
        saveDraft={saveDraft}
        emailClient={emailClient}
        onDownloadPDF={downloadPDF}
        onDownloadTearSheets={openTearSheetModal}
        saving={saving}
        proposalId={savedProposal?.proposalId}
        projectId={form.projectId}
        quickbooks={savedProposal?.quickbooks}
        isLocked={isLocked}
        onRequestEdit={requestEdit}
        canConvert={Boolean(savedProposal?.proposalId)}
        onConvert={convertToInvoice}
        onConvertToPO={convertToPurchaseOrder}
        canEdit={canEdit}
      />

      {savedProposal?.proposalId && (
        <ActivityFeed
          userId={userId}
          projectId={form.projectId}
          entityType="proposal"
          entityId={savedProposal.proposalId}
        />
      )}

      {error && <div className="po-error">{error}</div>}

      {showAddItemModal && (
        <div className="modal-backdrop">
          <div className="proposal-modal-content">
            <AddItemForm
              onClose={() => {
                setShowAddItemModal(false);
                refreshCatalog();
              }}
              refreshItems={refreshCatalog}
            />
          </div>
        </div>
      )}

      {showEditWarning && (
        <div className="proposal-lock-overlay" onClick={() => setShowEditWarning(false)}>
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Unlock proposal for editing?</h3>
            <p>
              Editing will revoke the Approved status if present. If payments were made, a credit
              will be created for the paid amount.
            </p>
            <div className="proposal-lock-actions">
              <button className="if-btn-secondary" onClick={() => setShowEditWarning(false)}>
                Cancel
              </button>
              <button className="if-btn-primary" onClick={revokeApproval} disabled={revoking}>
                {revoking ? 'Unlocking...' : 'Unlock & Edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {convertSource && (
        <ConvertToInvoiceModal
          items={convertSource.lineItems || []}
          paid={Number(convertSource.totals?.paid || 0)}
          onCancel={() => setConvertSource(null)}
          onConfirm={submitConversion}
        />
      )}

      {showTearSheetModal && (
        <TearSheetModal
          defaultTitle={project?.customerName || project?.name || form.projectSite || 'Tear Sheet'}
          onDownload={handleTearSheetDownload}
          onCancel={() => setShowTearSheetModal(false)}
          defaultShowVendor={proposalTemplate?.showVendor === true}
        />
      )}

      {tearSheetConfig && (
        <div className="ts-pdf-render-root" aria-hidden="true">
          <div ref={tearSheetPdfRef}>
            <TearSheetPdfDocument
              items={tearSheetItems || computedItems}
              sheetTitle={tearSheetConfig.title}
              company={company}
              options={tearSheetConfig.options}
            />
          </div>
        </div>
      )}

      {vendorModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setVendorModal({ open: false, vendors: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Multiple vendors found</h3>
            <p>
              This proposal includes items from multiple vendors. Create one purchase order per
              vendor?
              {unassignedItemsNotice(vendorModal.unassignedCount)}
            </p>
            <div style={{ marginBottom: 10 }}>
              {vendorModal.vendors.map((v) => (
                <span
                  key={v}
                  style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#eef2ff',
                    color: '#1e1b4b',
                    fontSize: 12,
                    marginRight: 6,
                    marginBottom: 6,
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setVendorModal({ open: false, vendors: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={confirmSplitProposalVendors}>
                Create Purchase Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {poBlockers && (
        <ConvertToPoBlockersModal
          proposalNumber={proposalNumber}
          blockers={poBlockers}
          actions={poBlockerActions}
          onClose={() => setPoBlockers(null)}
        />
      )}

      {sendEmailModal.open && (
        <SendEmailModal
          type="proposal"
          defaultEmail={sendEmailModal.email}
          clientName={form.client?.name || ''}
          docNumber={proposalNumber || ''}
          docUrl={sendEmailModal.publicUrl}
          companyName={project?.companyName || project?.name || ''}
          projectId={sendEmailModal.projectId}
          documentId={sendEmailModal.proposalId}
          onGenerateTearSheet={generateTearSheetForEmail}
          onClose={() => setSendEmailModal({ open: false, email: '', publicUrl: '', projectId: '', proposalId: '' })}
          onSent={() => notify(`Proposal sent to ${sendEmailModal.email}`)}
        />
      )}
    </div>
  );
}

ProposalForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  project: PropTypes.object,
  initial: PropTypes.object,
  depositToRecord: PropTypes.shape({ amount: PropTypes.number }),
};




